import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { EnvironmentLoaderService } from './environment-loader.service';
import { PagedResult } from '../models/paging';
import { PricePolicyType, PricePolicy, PricePolicySearchEvent } from 'src/app/features/prices/price-policies/interfaces/price-policy';

@Injectable({
  providedIn: 'root',
})
export class PricePolicyV2Service {
  private httpClient = inject(HttpClient);

  private readonly apiUrl: string;
  private readonly httpHeaders: HttpHeaders;

  constructor() {
    const environmentService = inject(EnvironmentLoaderService);

    this.apiUrl = <string>environmentService.config.serviceBaseUrl + '/api/v2/admin';

    this.httpHeaders = new HttpHeaders({
      'Access-Control-Allow-Origin': this.apiUrl,
      'Content-Type': 'application/json',
    });
  }

  public getPricePolicies(searchEvent: PricePolicySearchEvent): Observable<PagedResult<PricePolicy>> {
    const url = `${this.apiUrl}/price-policies`;
    let params = new HttpParams();
    const organizationId = Number(searchEvent?.organizationId);
    const pricePolicyId = Number(searchEvent?.pricePolicyId);

    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent?.onlyGlobal != null) {
      params = params.append('onlyGlobal', searchEvent.onlyGlobal);
    }
    if (Number.isFinite(organizationId) && organizationId > 0) {
      params = params.append('organizationId', organizationId);
    }
    if (Number.isFinite(pricePolicyId) && pricePolicyId > 0) {
      params = params.append('pricePolicyId', pricePolicyId);
    }
    if (searchEvent?.type) {
      params = params.append('type', searchEvent.type);
    }
    if (searchEvent?.includeGlobal != null) {
      params = params.append('includeGlobal', searchEvent.includeGlobal);
    }
    if (searchEvent?.alwaysIncludeDynamic != null) {
      params = params.append('alwaysIncludeDynamic', searchEvent.alwaysIncludeDynamic);
    }
    if (searchEvent.paging?.pageIndex) {
      params = params.append('page', searchEvent.paging.pageIndex);
    }
    if (searchEvent.paging?.pageSize) {
      params = params.append('pageSize', searchEvent.paging.pageSize);
    }
    if (searchEvent.sorting?.active) {
      params = params.append('sortBy', searchEvent.sorting?.active);
    }
    if (searchEvent.sorting?.direction) {
      params = params.append('sortDirection', searchEvent.sorting?.direction);
    }

    return this.httpClient.get<PagedResult<PricePolicy>>(url, {
      params,
    });
  }

  public getPricePolicy(pricePolicyId: number): Observable<PricePolicy> {
    const url = `${this.apiUrl}/price-policies`;
    const params = new HttpParams().append('pricePolicyId', pricePolicyId);

    return this.httpClient.get<PagedResult<PricePolicy>>(url, { params }).pipe(map(result => result.items[0]));
  }

  public createPricePolicy(request: CreatePricePolicyRequest): Observable<number> {
    return this.httpClient.post<number>(`${this.apiUrl}/price-policies`, request, {
      headers: this.httpHeaders,
    });
  }

  public updatePricePolicy(request: UpdatePricePolicyRequest): Observable<number> {
    return this.httpClient.put<number>(`${this.apiUrl}/price-policies/${request.pricePolicyId}`, request, {
      headers: this.httpHeaders,
    });
  }

  public deletePricePolicy(pricePolicyId: number, note: string) {
    return this.httpClient.post(
      `${this.apiUrl}/price-policies/${pricePolicyId}/delete`,
      { note: note },
      {
        headers: this.httpHeaders,
      }
    );
  }
}

interface PricePolicyTypeFields {
  type: PricePolicyType;
  pricePerUnit?: number | null;
  lowLimit?: number | null;
  highLimit?: number | null;
  fallbackPricePolicy?: number | null;
}

function buildTypeFields(fields: PricePolicyTypeFields) {
  if (fields.type === PricePolicyType.FixedWithOkMeter || fields.type === PricePolicyType.FixedWithoutOkMeter) {
    return {
      fixed: { pricePerKwh: fields.pricePerUnit ?? 0 },
      dynamic: null,
    };
  }
  if (fields.type === PricePolicyType.Dynamic) {
    return {
      fixed: null,
      dynamic: {
        lowLimit: fields.lowLimit ?? 0,
        highLimit: fields.highLimit ?? 0,
        fallbackPricePolicyId: fields.fallbackPricePolicy ?? undefined,
      },
    };
  }
  return { fixed: null, dynamic: null };
}

export class CreatePricePolicyRequest {
  name: string;
  fixed: { pricePerKwh: number } | null;
  dynamic: { lowLimit: number; highLimit: number; fallbackPricePolicyId?: number } | null;
  okSupplement: number | null;
  organizationSupplement: number | null;
  organizationId?: number | null;
  note: string;
  policyType: PricePolicyType;

  constructor(
    form: {
      name?: string;
      organization?: { id: number } | null;
      okSupplement?: number | null;
      organizationSupplement?: number | null;
      type: PricePolicyType;
    } & PricePolicyTypeFields,
    note: string
  ) {
    this.name = form.name!;
    this.organizationId = form.organization?.id;
    this.okSupplement = form.okSupplement ?? null;
    this.organizationSupplement = form.organizationSupplement ?? null;
    this.note = note;
    this.policyType = form.type;
    const { fixed, dynamic } = buildTypeFields(form);
    this.fixed = fixed;
    this.dynamic = dynamic;
  }
}

export class UpdatePricePolicyRequest {
  pricePolicyId: number;
  name: string;
  fixed: { pricePerKwh: number } | null;
  dynamic: { lowLimit: number; highLimit: number; fallbackPricePolicyId?: number } | null;
  okSupplement: number | null;
  organizationSupplement: number | null;
  note: string;

  constructor(
    pricePolicyId: number,
    form: { name?: string; okSupplement?: number | null; organizationSupplement?: number | null } & PricePolicyTypeFields,
    note: string
  ) {
    this.pricePolicyId = pricePolicyId;
    this.name = form.name!;
    this.okSupplement = form.okSupplement ?? null;
    this.organizationSupplement = form.organizationSupplement ?? null;
    this.note = note;
    const { fixed, dynamic } = buildTypeFields(form);
    this.fixed = fixed;
    this.dynamic = dynamic;
  }
}
